import { Component, Host, State, h } from '@stencil/core';
import spacetime from 'spacetime';

import { schedule } from '../../store/schedule.store';
import { selection } from '../../store/selection.store';
import { settings } from '../../store/settings.store';

import { Category } from '@artask/tambr-models/models/category.model';
import { Location } from '@artask/tambr-models/models/location.model';



@Component({
  tag: 'tambr-calendar',
  styleUrl: 'tambr-calendar.css',
  shadow: true,
})
export class TambrCalendar {

  @State() selectedCategoryId: string;
  @State() selectedLocationId: string;

  get selectedSpaces(): string[] {
    if ( settings.spaceId ) return [ settings.spaceId ];

    let spaces = schedule.dateSchedule.spaces;

    if ( settings.locationId ) {
      spaces = spaces.filter( space => space.locationId === settings.locationId );
    }

    if ( settings.categoryId ) {
      spaces = spaces.filter( space => space.categoryId === settings.categoryId );
    }

    if ( this.selectedCategoryId ) {
      spaces = spaces.filter( space => space.categoryId === this.selectedCategoryId );
    }

    if ( this.selectedLocationId ) {
      spaces = spaces.filter( space => space.locationId === this.selectedLocationId );
    }

    return spaces.map( space => space.id );
  }


  get getCategories(): Category[] {
    return schedule.dateSchedule.categories.filter( category => 
      schedule.dateSchedule.spaces.some( space => {
        // exclude categories not used in the location
        if ( settings.locationId && (space.locationId !== settings.locationId) ) return false;
        return space.categoryId === category.id;
      }) 
    );
  }


  get getLocations(): Location[] {
    return schedule.dateSchedule.locations.filter( location =>
      schedule.dateSchedule.spaces.some( space => {
        // exclude locations not used in the category
        if ( settings.categoryId && (space.categoryId !== settings.categoryId) ) return false;
        return space.locationId === location.id;
      } )
    );
  } 


  selectCategory( categoryId: string | null ) {
    this.selectedCategoryId = categoryId;
  }


  selectLocation( locationId: string | null ) {
    this.selectedLocationId = locationId;
  }


  render() {
    return (
      <Host>
        { this.renderFilter() }

        { this.renderCalendarSubheader() }

        <div class={ 'calendar-wrapper ' + settings.selectedView }>
          
          { schedule.dateScheduleLoading && <div class='loading-overlay'></div> }
          
          <tambr-calendar-grid-view 
            class='tambr-calendar-view'
            selectedSpaces={ this.selectedSpaces }
            categories={ this.getCategories } />
    
          <tambr-calendar-card-view
            class='tambr-calendar-view'
            selectedSpaces={ this.selectedSpaces } />

        </div>
      </Host>
    );
  }


  renderFilter() {
    return (
      <div class="actions-container">
        <div class='filter-container'>
          { this.renderLocationFilter() }
          { this.renderCategoryFilter() }
        </div>
      </div>
    );
  }


  renderCategoryFilter() {
    if ( !schedule.dateSchedule.categories.length || settings.spaceId || settings.categoryId ) return;          
    if ( this.getCategories.length < 2 ) return;
    const allSpacesButtonClass = {
      'tambr-stroked-button': true,
      'active': !this.selectedCategoryId
    }
    return (
      <div class='filter-buttons'>
        <button class={ allSpacesButtonClass } onClick={ () => this.selectCategory(null) }>All studios</button>          
        { this.getCategories.map( ( category: Category ) => this.renderCategoryButton( category ) ) }
      </div>
    );
  }


  renderLocationFilter() {
    if ( !schedule.dateSchedule?.locations?.length || settings.spaceId || settings.locationId ) return;
    if ( this.getLocations.length < 2 ) return;
    const allSpacesButtonClass = {
      'tambr-stroked-button': true,
      'active': !this.selectedLocationId
    }
    return (
      <div class='filter-buttons'>
        <button class={ allSpacesButtonClass } onClick={ () => this.selectLocation(null) }>All locations</button>
        { this.getLocations.map( ( location: Location ) => this.renderLocationButton( location ) ) }
      </div>
    );
  }

  renderCategoryButton( category: Category ) {
    const isDisabled = this.selectedLocationId && !schedule.dateSchedule.spaces.some( space => space.locationId === this.selectedLocationId && space.categoryId === category.id );
    const buttonClasses = {
      'tambr-stroked-button': true,
      'active': this.selectedCategoryId === category.id
    }
    return <button class={ buttonClasses } onClick={ () => this.selectCategory(category.id) } disabled={ isDisabled }>{ category.title }</button>;
  }
  

  renderLocationButton( location: Location ) {
    const isDisabled = this.selectedCategoryId && !schedule.dateSchedule.spaces.some( space => space.categoryId === this.selectedCategoryId && space.locationId === location.id );
    const buttonClasses = {
      'tambr-stroked-button': true,
      'active': this.selectedLocationId === location.id
    }
    return <button class={ buttonClasses } onClick={ () => this.selectLocation(location.id) } disabled={ isDisabled }>{ location.title }</button>;
  }


  renderCalendarSubheader() {
    return (
      <div class='calendar-subheader'>
        <div class='selected-date'>
          { this.renderSelectedDate() }
        </div>
        <div class='view-switch'>
          { this.renderViewSwitch() }
        </div>
      </div>
    );
  }


  renderSelectedDate() {
    if ( !selection.date || schedule.dateScheduleLoading ) return '';
    return ([
      <span class='desktop-date'>{ spacetime( selection.date ).format('{day}, {month} {date-ordinal}') }</span>,
      <span class='mobile-date'>{ spacetime( selection.date ).format('{day-short}, {month-short} {date}') }</span>
    ])
  }
  

  renderViewSwitch() {
    return(
      <div class={ 'view-btns ' + ( settings.appCheckFailed ? 'hidden' : '' ) }>
        <button onClick={ () => settings.selectedView = 'grid' }
          class={'tambr-view-button ' + ( settings.selectedView === 'grid' ? 'active' : '' ) }>
            <tambr-icon type='grid'></tambr-icon>
            <span>Grid view</span>
        </button>
        <button onClick={ () => settings.selectedView = 'card' }
          class={ 'tambr-view-button ' + ( settings.selectedView === 'card' ? 'active' : '' ) }>
            <tambr-icon type='card'></tambr-icon>
            <span>Card view</span>
        </button>
      </div>
    );
  }

}
